/**
 * useStickyScroll
 *
 * Keeps a scroll container pinned to the bottom as content streams in, without
 * fighting the user.
 *
 * Two problems this exists to solve, both of which make a streaming chat feel
 * broken:
 *
 * 1. Calling scrollIntoView({behavior: "smooth"}) on every token starts a new
 *    animation before the previous one finishes. At token rate that is dozens
 *    of interrupted animations a second, which reads as stutter. Sticking to
 *    the bottom during a stream must be instant; only discrete events (a new
 *    message arriving) deserve a smooth scroll.
 *
 * 2. Unconditional auto-scroll makes it impossible to read back through the
 *    conversation while a reply is streaming — every token yanks you to the
 *    bottom. Auto-scroll must therefore be conditional on the user already
 *    being at the bottom, and must stop the moment they scroll away.
 *
 * Scrolls are coalesced into a single animation frame so a burst of tokens
 * produces one scroll, not one per token.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/** How close to the bottom still counts as "at the bottom", in pixels. */
const BOTTOM_THRESHOLD_PX = 64;

export interface StickyScrollResult {
  /** Attach to the scrollable element. */
  containerRef: React.RefObject<HTMLDivElement>;
  /** True when the user has scrolled away from the bottom. */
  isPinnedToBottom: boolean;
  /** Scroll to the bottom. Smooth by default; pass false while streaming. */
  scrollToBottom: (smooth?: boolean) => void;
  /** Call on the container's onScroll. */
  handleScroll: () => void;
}

export function useStickyScroll(
  /** Values that should trigger a stick-to-bottom check when they change. */
  deps: readonly unknown[],
  options: { enabled?: boolean } = {}
): StickyScrollResult {
  const { enabled = true } = options;

  const containerRef = useRef<HTMLDivElement>(null);
  // Ref, not state: the scroll handler reads this on every scroll event and
  // must not cause a re-render each time.
  const shouldStickRef = useRef(true);
  const frameRef = useRef<number | null>(null);
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);

  const measureAtBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return true;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distance <= BOTTOM_THRESHOLD_PX;
  }, []);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = containerRef.current;
    if (!el) return;

    // Re-pin: an explicit request means the user wants to follow along again.
    shouldStickRef.current = true;
    setIsPinnedToBottom(true);

    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth ? "smooth" : "auto",
    });
  }, []);

  const handleScroll = useCallback(() => {
    const atBottom = measureAtBottom();
    shouldStickRef.current = atBottom;
    // Only re-render when the answer actually flips.
    setIsPinnedToBottom((previous) =>
      previous === atBottom ? previous : atBottom
    );
  }, [measureAtBottom]);

  // useLayoutEffect so the scroll happens in the same frame the new content
  // paints; useEffect would let a half-scrolled frame render first.
  useLayoutEffect(() => {
    if (!enabled) return;
    if (!shouldStickRef.current) return;

    const el = containerRef.current;
    if (!el) return;

    // Coalesce a burst of token updates into one scroll per frame.
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const current = containerRef.current;
      if (!current) return;
      // Instant: this fires at token rate, and smooth would queue competing
      // animations that never settle.
      current.scrollTop = current.scrollHeight;
    });

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  useEffect(() => {
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  return { containerRef, isPinnedToBottom, scrollToBottom, handleScroll };
}
